interface ThresholdRanges {
  danger: number
  warning: number
}

interface ThresholdLabels {
  danger: string
  warning: string
  normal: string
}

type Severity = "danger" | "warning" | "normal"

const severityStyles: Record<
  Severity,
  { title: string; value: string; label: string; container: string }
> = {
  danger: {
    title: "text-coval-text-muted",
    label: "text-coval-danger-on-surface",
    value: "text-coval-danger-on-surface",
    container: "border-coval-danger-border bg-coval-danger-surface",
  },
  warning: {
    title: "text-coval-text-muted",
    label: "text-coval-warning-on-surface",
    value: "text-coval-warning-on-surface",
    container: "border-coval-warning-border bg-coval-warning-surface",
  },
  normal: {
    title: "text-coval-text-muted",
    label: "text-coval-text-muted",
    value: "text-coval-text",
    container: "border-coval-border bg-surface-default",
  },
}

const defaultStyles = {
  title: "text-coval-text-muted",
  label: "text-coval-text-muted",
  value: "text-coval-text",
  container: "border-coval-border bg-surface-default",
} as const

function getSeverity(value: number, ranges: ThresholdRanges): Severity {
  if (value > ranges.danger) {
    return "danger"
  }
  if (value > ranges.warning) {
    return "warning"
  }
  return "normal"
}

interface ThresholdMetricBoxProps {
  title: string
  value: number | null | undefined
  ranges?: ThresholdRanges
  labels?: ThresholdLabels
  formatValue?: (value: number) => string
  unit?: string
}

export function ThresholdMetricBox({
  title,
  value,
  ranges,
  labels,
  formatValue = (v) => v.toFixed(2),
  unit,
}: ThresholdMetricBoxProps) {
  const hasValue = value != null && Number.isFinite(value)
  const styles =
    hasValue && ranges
      ? severityStyles[getSeverity(value, ranges)]
      : defaultStyles
  const label =
    hasValue && ranges && labels
      ? labels[getSeverity(value, ranges)]
      : undefined
  return (
    <div className={`p-3 rounded-lg border ${styles.container}`}>
      <div className="flex items-baseline justify-between mb-1">
        <span className={`text-sm font-medium ${styles.title}`}>{title}</span>
        {label && (
          <span className={`text-xs font-normal ${styles.label}`}>{label}</span>
        )}
      </div>
      <div className={`text-2xl font-bold ${styles.value}`}>
        {hasValue ? formatValue(value) : "--"}
        {hasValue && unit && (
          <span className="text-sm font-normal ml-1">{unit}</span>
        )}
      </div>
    </div>
  )
}
